import { memberAdapter } from '@skop-app/lib-domain/adapters'
import {
  LabelWithRole,
  Language,
  Location,
  Progress,
  Project,
  ProjectMember,
} from '@skop-app/lib-domain/types'
import { sortBy } from 'lodash'
import { isTruthy } from 'remeda'

import { ProjectSchema } from '@/types'
import { formatAddress } from '@/utils/helpers'
import {
  type ProjectTrackingProfile,
  readProjectTrackingProfilePreference,
} from '@/features/project/projectTrackingProfile'

import { sectionAdapter } from './section'

export type ProjectCardData = {
  label: string
  value: string
  picture: string
  progress: string
  startDate: Date | undefined
  endDate: Date | undefined
  formatedDate: string
  place: string
  address: string | null
  placeID: string
  location: Location
  organizationID: string
  userProjectsId?: string | null
  type?: string | null
  preferences?: (string | null)[] | null
  members: Omit<LabelWithRole, 'organizationID'>[]
  catalog: string
  updatedAt: string
  sections: ReturnType<typeof sectionAdapter>[]
  isGuest?: boolean
}

const isVisibleProjectMember = (member?: { status?: string | null } | null) =>
  member?.status !== 'ARCHIVED'

// with a try catch for each member
const memberAdapterWithTryCatch = (member) => {
  try {
    return {
      label: member.member?.name ?? member.id,
      value: member.id,
      userID: member.userID,
      role: member.role,
    }
  } catch (error) {
    console.error(error)
    return undefined
  }
}

export const projectCardAdapter = (
  project: ProjectSchema,
  lang: Language,
  isGuest?: boolean,
): ProjectCardData => {
  const sections =
    project.blueprints.items?.flatMap((blueprint) =>
      (blueprint?.sections?.items ?? [])
        .filter(isTruthy)
        .map((section) => sectionAdapter(section, lang)),
    ) || []

  const members = project.members.items
    .filter(isVisibleProjectMember)
    .map((m) => memberAdapterWithTryCatch(m))
    .filter(isTruthy)

  return {
    label: project.name,
    value: project.id,
    picture: project.picture || 'default-project.png',
    progress: project.progress || 'active',
    startDate: project.startDate ? new Date(project.startDate) : undefined,
    endDate: project.endDate ? new Date(project.endDate) : undefined,
    formatedDate: formatProjectDate(project.startDate, project.endDate),
    place: project.place?.name,
    address: formatAddress({
      address: project.place?.address,
      zipcode: project.place?.zipcode,
      city: project.place?.city,
      country: project.place?.country,
    }),
    placeID: project.place?.id,
    location: project.place?.location,
    organizationID: project.organizationID,
    userProjectsId: project.userProjectsId,
    type: project.type,
    preferences: project.preferences,
    members,
    catalog: project.catalogs?.items?.[0]?.id,
    updatedAt: project.updatedAt,
    sections,
    isGuest,
  }
}

export type AdaptedProject = Omit<
  Project,
  'members' | 'placeProjectsId' | 'userProjectsId'
> & {
  members: Partial<ProjectMember>[]
  trackingProfile?: ProjectTrackingProfile | null
  preferences?: string[]
  actualSiteTimeInsertionMinutes?: number | null
  actualSiteTimeClassicMinutes?: number | null
  actualSiteTimeTotalMinutes?: number | null
}

export const projectAdapter = (project: ProjectSchema): AdaptedProject => {
  const members =
    project?.members?.items
      .filter(isVisibleProjectMember)
      .map((member) => memberAdapter(member)) || []
  const sortedMembers = sortBy(members, ['createdAt'])
  const actualSiteTimeInsertionMinutes =
    typeof project.actualSiteTimeInsertionMinutes === 'number'
      ? project.actualSiteTimeInsertionMinutes
      : null
  const actualSiteTimeClassicMinutes =
    typeof project.actualSiteTimeClassicMinutes === 'number'
      ? project.actualSiteTimeClassicMinutes
      : null
  const actualSiteTimeTotalMinutes =
    actualSiteTimeInsertionMinutes !== null ||
    actualSiteTimeClassicMinutes !== null
      ? (actualSiteTimeInsertionMinutes ?? 0) +
        (actualSiteTimeClassicMinutes ?? 0)
      : null

  return {
    ...project,
    progress: (project.progress || 'active') as Progress,
    trackingProfile:
      readProjectTrackingProfilePreference(project.preferences) ?? null,
    preferences: project.preferences ?? [],
    actualSiteTimeInsertionMinutes,
    actualSiteTimeClassicMinutes,
    actualSiteTimeTotalMinutes,
    startDate: project.startDate ? new Date(project.startDate) : null,
    endDate: project.endDate ? new Date(project.endDate) : null,
    picture: project.picture || 'default-project.png',
    members: sortedMembers,
    blueprints: project.blueprints.items || [],
    createdAt: project.createdAt,
    place: project.place,
    catalogs: project.catalogs?.items || [],
  }
}

const formatProjectDate = (startDate, endDate) => {
  if (!startDate && !endDate) {
    return 'Date non définie'
  }
  if (startDate && !endDate) {
    return `A partir du ${formatDate(startDate)}`
  }
  if (!startDate && endDate) {
    return `Jusqu'au ${formatDate(endDate)}`
  }
  return `${formatDate(startDate)} - ${formatDate(endDate)}`
}

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
